// ─── PhonePreviewModal ────────────────────────────────────────
// Pure modal showing a QR + URL + renderer info. Extracted from
// PreviewOnPhoneButton (Phase 1.5) so it can be opened from multiple
// surfaces (Studio toolbar, Event Setup panel).
//
// Read-only — does not mutate invitation data, does not create a guest
// session, does not expose backup or admin-only params.

import React, { useEffect, useState, useCallback } from 'react';
import QRCodeLib from 'qrcode';
import { X, ExternalLink, Loader2 } from 'lucide-react';

/**
 * Build the public URL. If the invitation isn't published yet and a preview
 * token is available, fall back to the draft preview URL — but never inject
 * admin-only query params.
 */
export function buildPublicPreviewUrl({ slug, isPublished, previewToken }) {
  if (!slug || typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const base = `${origin}/invite/${slug}`;
  if (!isPublished && previewToken) {
    return `${base}?preview=${encodeURIComponent(previewToken)}`;
  }
  return base;
}

export default function PhonePreviewModal({
  slug,
  layoutVersion,
  layoutMode,
  updatedAt,
  isPublished,
  previewToken,
  onClose,
}) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = buildPublicPreviewUrl({ slug, isPublished, previewToken });

  const generate = useCallback(async () => {
    if (!url) return;
    setBusy(true);
    try {
      const data = await QRCodeLib.toDataURL(url, {
        width: 256,
        margin: 2,
        color: { dark: '#1a1a1a', light: '#ffffff' },
      });
      setQrDataUrl(data);
    } catch {
      setQrDataUrl(null);
    } finally {
      setBusy(false);
    }
  }, [url]);

  useEffect(() => { generate(); }, [generate]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!slug) return null;

  const versionLabel = layoutVersion === 'section-v1' ? 'section-v1' : 'legacy-flat';
  const updatedLabel = updatedAt ? new Date(updatedAt).toLocaleString() : null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="preview-on-phone-modal"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
          aria-label="Close"
          data-testid="preview-on-phone-close"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold text-gray-900 mb-1">Preview on phone</h3>
        <p className="text-xs text-gray-500 mb-4">
          Scan to check the invitation exactly as guests will see it.
        </p>

        <div className="flex justify-center mb-4 bg-gray-50 rounded-xl p-4">
          {busy && !qrDataUrl ? (
            <div className="w-[256px] h-[256px] flex items-center justify-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR code linking to the public invitation"
              width={256}
              height={256}
              data-testid="preview-on-phone-qr"
            />
          ) : (
            <div className="w-[256px] h-[256px] flex items-center justify-center text-xs text-gray-400">
              Could not generate QR
            </div>
          )}
        </div>

        <div className="text-[11px] text-gray-500 break-all mb-3" data-testid="preview-on-phone-url">
          {url}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 flex-wrap">
            <span
              className={`px-1.5 py-0.5 rounded-full uppercase tracking-wide ${
                layoutVersion === 'section-v1'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
              data-testid="preview-layout-version"
            >
              {versionLabel}
            </span>
            {layoutMode && (
              <span
                className="px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-gray-100 text-gray-600"
                data-testid="preview-layout-mode"
              >
                {layoutMode}
              </span>
            )}
            {!isPublished && (
              <span className="px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-amber-100 text-amber-700">
                draft
              </span>
            )}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
            data-testid="preview-on-phone-open"
          >
            Open in new tab <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {updatedLabel && (
          <p className="mt-3 text-[10px] text-gray-400" data-testid="preview-on-phone-updated">
            Last saved {updatedLabel}
          </p>
        )}
      </div>
    </div>
  );
}
